import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Mail, 
  Copy, 
  RefreshCw, 
  Trash2, 
  Clock, 
  CheckCircle2, 
  XCircle,
  AlertCircle,
  Link as LinkIcon,
  Loader2,
  Send
} from 'lucide-react';
import api from '../lib/api';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Amministratore' },
  { value: 'amministrazione', label: 'Amministrazione' },
  { value: 'coordinator', label: 'Coordinatore' },
  { value: 'pm', label: 'Project Manager' },
  { value: 'risorsa', label: 'Risorsa' }
];

const STATUS_CONFIG = {
  pending: { label: 'In Attesa', variant: 'default', icon: Clock },
  accepted: { label: 'Accettato', variant: 'success', icon: CheckCircle2 },
  expired: { label: 'Scaduto', variant: 'destructive', icon: AlertCircle },
  cancelled: { label: 'Annullato', variant: 'secondary', icon: XCircle }
};

export const GestioneInvitiPage = () => {
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'risorsa' });
  const [createdInvitation, setCreatedInvitation] = useState(null);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const response = await api.get('/invitations');
      setInvitations(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli inviti');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    
    if (!newInvitation.email || !newInvitation.role) {
      toast.error('Compila tutti i campi');
      return;
    }

    setIsCreating(true);
    try {
      const response = await api.post('/invitations', newInvitation);
      setCreatedInvitation(response.data);
      toast.success('Invito creato con successo');
      loadInvitations();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella creazione dell\'invito';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleResend = async (invitationId) => {
    try {
      const response = await api.post(`/invitations/${invitationId}/resend`);
      toast.success('Invito rinnovato', {
        description: 'È stato generato un nuovo link di registrazione'
      });
      
      // Show the new link
      setCreatedInvitation({
        registration_link: response.data.registration_link,
        email: invitations.find(i => i.id === invitationId)?.email
      });
      
      loadInvitations();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel rinnovo dell\'invito';
      toast.error(message);
    }
  };

  const handleCancel = async (invitationId) => {
    if (!confirm('Sei sicuro di voler annullare questo invito?')) return;
    
    try {
      await api.delete(`/invitations/${invitationId}`);
      toast.success('Invito annullato');
      loadInvitations();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'annullamento dell\'invito';
      toast.error(message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copiato negli appunti');
  };

  const resetDialog = () => {
    setNewInvitation({ email: '', role: 'risorsa' });
    setCreatedInvitation(null);
    setIsDialogOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const pendingCount = invitations.filter(i => i.status === 'pending').length;
  const acceptedCount = invitations.filter(i => i.status === 'accepted').length;

  return (
    <div className="space-y-6" data-testid="gestione-inviti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Gestione Inviti</h1>
          <p className="text-muted-foreground">Invita nuovi utenti alla piattaforma</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-invitation-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Nuovo Invito
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {createdInvitation ? 'Invito Creato' : 'Crea Nuovo Invito'}
              </DialogTitle>
              <DialogDescription>
                {createdInvitation 
                  ? 'Copia il link di registrazione e invialo all\'utente'
                  : 'Inserisci email e ruolo dell\'utente da invitare'
                }
              </DialogDescription>
            </DialogHeader>
            
            {createdInvitation ? (
              <div className="space-y-4">
                <Alert className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-800 dark:text-emerald-200">
                    Invito creato per <strong>{createdInvitation.email}</strong>
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2">
                  <Label>Link di Registrazione</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={createdInvitation.registration_link} 
                      readOnly 
                      className="font-mono text-sm"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => copyToClipboard(createdInvitation.registration_link)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Il link scade tra 48 ore. Copialo e invialo all'utente.
                  </p>
                </div>
                
                <DialogFooter>
                  <Button onClick={resetDialog}>Chiudi</Button>
                </DialogFooter>
              </div>
            ) : (
              <form onSubmit={handleCreateInvitation} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email *</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="nome@azienda.it"
                    value={newInvitation.email}
                    onChange={(e) => setNewInvitation({ ...newInvitation, email: e.target.value })}
                    required
                    data-testid="invite-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="invite-role">Ruolo *</Label>
                  <Select 
                    value={newInvitation.role} 
                    onValueChange={(value) => setNewInvitation({ ...newInvitation, role: value })}
                  >
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue placeholder="Seleziona ruolo" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map(role => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={resetDialog}>
                    Annulla
                  </Button>
                  <Button type="submit" disabled={isCreating} data-testid="submit-invitation-btn">
                    {isCreating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creazione...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Crea Invito
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{invitations.length}</div>
            <p className="text-xs text-muted-foreground">Totale Inviti</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">In Attesa</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-emerald-600">{acceptedCount}</div>
            <p className="text-xs text-muted-foreground">Accettati</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-slate-400">
              {invitations.filter(i => i.status === 'expired' || i.status === 'cancelled').length}
            </div>
            <p className="text-xs text-muted-foreground">Scaduti/Annullati</p>
          </CardContent>
        </Card>
      </div>

      {/* Invitations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista Inviti</CardTitle>
          <CardDescription>
            Gestisci gli inviti inviati agli utenti
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nessun invito creato</p>
              <p className="text-sm text-muted-foreground mt-1">
                Clicca su "Nuovo Invito" per invitare un utente
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Ruolo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Creato da</TableHead>
                    <TableHead>Scadenza</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow key={invitation.id} data-testid={`invitation-row-${invitation.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {invitation.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {ROLE_OPTIONS.find(r => r.value === invitation.role)?.label || invitation.role}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(invitation.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {invitation.created_by_name}
                      </TableCell>
                      <TableCell className="text-sm">
                        {invitation.status === 'accepted' 
                          ? formatDate(invitation.accepted_at)
                          : formatDate(invitation.expires_at)
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {invitation.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(invitation.registration_link)}
                                title="Copia link"
                              >
                                <LinkIcon className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResend(invitation.id)}
                                title="Rinnova invito"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancel(invitation.id)}
                                className="text-red-600 hover:text-red-700"
                                title="Annulla invito"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {(invitation.status === 'expired' || invitation.status === 'cancelled') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResend(invitation.id)}
                              title="Rinnova invito"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GestioneInvitiPage;
